import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { ExploreHeader, ExploreHolding, ExploreError } from "@/attendee/ExploreShared";
import { AlertTriangle, Clock, Waves, ChevronDown, ListChecks } from "lucide-react";

export default function ExplorePools() {
  const { eventId } = useApp();
  const [doc, setDoc] = useState(null);
  const [state, setState] = useState("loading");
  const [rulesOpen, setRulesOpen] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);

  const load = useCallback(() => {
    let active = true;
    setState("loading");
    setDoc(null);
    api.explorePools(eventId)
      .then((d) => { if (active) { if (d.activated === false) { setState("holding"); } else { setDoc((d.items || [])[0] || null); setState("ok"); } } })
      .catch(() => { if (active) setState("error"); });
    return () => { active = false; };
  }, [eventId]);

  useEffect(() => load(), [load]);

  if (state === "error") return <ExploreError onRetry={load} />;
  if (state === "holding") return <ExploreHolding />;
  if (!doc) return <div className="pt-20 px-4"><div className="h-48 att-card animate-pulse" /></div>;

  return (
    <div className="pt-16 px-4 pb-8">
      <ExploreHeader eyebrow="Explore" title="Pools & Beach" subtitle={doc.subtitle} />

      {/* Ocean prohibition */}
      <div className="att-card overflow-hidden mb-4 border-l-2 border-[hsl(var(--pc-gold))]" data-testid="pools-ocean-notice">
        <div className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-[hsl(var(--pc-gold))] shrink-0 mt-0.5" />
            <div>
              <h2 className="font-serif text-xl font-bold text-cream leading-tight">Ocean entry is strictly prohibited.</h2>
              <p className="text-sm text-cream-dim leading-relaxed mt-1">Do not enter the ocean at the beach adjacent to the hotel. You may enjoy walks along the seashore.</p>
            </div>
          </div>
        </div>
        {doc.ocean_notice && (
          <>
            <button
              onClick={() => setNoticeOpen((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3 text-left border-t border-[hsl(var(--pc-line))]"
              data-testid="pools-safety-toggle"
              aria-expanded={noticeOpen}
            >
              <span className="text-sm font-semibold text-cream">Read safety notice</span>
              <ChevronDown className={`h-4 w-4 text-[hsl(var(--pc-gold))] transition-transform duration-200 ${noticeOpen ? "rotate-180" : ""}`} />
            </button>
            {noticeOpen && (
              <p className="px-4 pb-4 pt-0 text-sm text-cream-dim leading-relaxed">{doc.ocean_notice}</p>
            )}
          </>
        )}
      </div>

      {/* Three distinct schedules */}
      <div className="att-card p-4 mb-4" data-testid="pools-hours">
        <div className="text-[11px] uppercase tracking-wide font-semibold text-cream-dim mb-2 inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-[hsl(var(--pc-gold))]" /> Hours</div>
        <div className="space-y-2">
          {(doc.facility_hours || []).map((f, i) => (
            <div key={i} className="flex items-center justify-between text-sm">
              <span className="text-cream-dim">{f.label}</span>
              <span className="text-cream font-medium">{f.detail}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Facilities */}
      {doc.facilities?.length > 0 && (
        <div className="att-card p-4 mb-4">
          <div className="text-[11px] uppercase tracking-wide font-semibold text-cream-dim mb-2 inline-flex items-center gap-1.5"><Waves className="h-3.5 w-3.5 text-[hsl(var(--pc-gold))]" /> Facilities</div>
          <div className="grid grid-cols-1 gap-1.5">
            {doc.facilities.map((f, i) => (
              <div key={i} className="text-sm text-cream flex items-start gap-2"><span className="text-[hsl(var(--pc-gold))] mt-1">•</span> {f}</div>
            ))}
          </div>
        </div>
      )}

      {doc.beach_note && (
        <div className="att-card p-4 mb-4">
          <div className="text-[11px] uppercase tracking-wide font-semibold text-cream-dim mb-1">The beach</div>
          <p className="text-sm text-cream-dim leading-relaxed">{doc.beach_note}</p>
        </div>
      )}

      {/* Pool rules */}
      {doc.rules?.length > 0 && (
        <div className="att-card overflow-hidden" data-testid="pools-rules">
          <button onClick={() => setRulesOpen((v) => !v)} className="w-full flex items-center justify-between px-4 py-3 text-left" aria-expanded={rulesOpen}>
            <span className="inline-flex items-center gap-2 font-semibold text-cream"><ListChecks className="h-4 w-4 text-[hsl(var(--pc-gold))]" /> Pool rules</span>
            <ChevronDown className={`h-4 w-4 text-[hsl(var(--pc-gold))] transition-transform duration-200 ${rulesOpen ? "rotate-180" : ""}`} />
          </button>
          {rulesOpen && (
            <ul className="px-4 pb-4 border-t border-[hsl(var(--pc-line))] pt-2 space-y-2">
              {doc.rules.map((r, i) => (
                <li key={i} className="text-sm text-cream-dim flex items-start gap-2"><span className="text-[hsl(var(--pc-gold))] mt-1">•</span> {r}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
